import {useDispatch, useSelector} from "react-redux";
import {useEffect, useState} from "react";
import {APPROVAL_TRACKER_MODEL, canRequestChanges} from "../Model/Model";
import {useTranslation} from "react-i18next";
import {useLocation, useNavigate, useParams} from "react-router-dom";
import {
    GetApprovalTrackerProgress,
    UpdateApprovalTrackerFlow
} from "../../../../reduxToolkit/ApprovalTracker/ApprovalTrackerApi";
import {errorMsg} from "../../../../Apis/functions";
import {STATUS_MODEL} from "../../../../enums/statusEnum";
import {canReadApprovalTrackerModule, getApprovalTrackerBackUrl} from "../Model/Model";

/**
 * @Email : azapata@ibl.mx
 * @Author: Ferro Zapata Arnulfo
 * @jobTittle: Full Stack Developer
 *
 * ApprovalTrackerApp Hook
 *
 * Custom React hook that handles approval tracking logic, including loading tracker data,
 * controlling modal states (approve, deny, request changes), and managing related user interaction state.
 *
 * This hook is typically used in pages that need to display or manage approval flow progress for a specific module.
 *
 * */


export const ApprovalTrackerApp = ()=>{
    const { t } = useTranslation();
    const navigate = useNavigate();
    const {module,subjectId,approvalFlowId} = useParams()
    const dispatch = useDispatch(); // Redux dispatch function
    const location = useLocation();
    let { state } = location;
    const backUrl = state?.backUrl;
    // Stamp sent by the notification on every click, so re-entering the flow the
    // view is already showing still counts as a new navigation
    const refreshedAt = state?.refreshedAt;
    const isOverdue = backUrl?.state?.isOverdue;
    const isCanceled = backUrl?.state?.isCanceled;
    const { getApprovalTrackerProgress } = useSelector((state) => state.ApprovalTrackerSlice);
    const { user, permission } = useSelector((state) => state.authenticatioauthennSlice);
    const DEV = useSelector((state) => state?.CommonsSlice?.development);
    // The tracker is a detail of the module in the url, so it is opened by the
    // read access of the views that link to it; reading another module is not
    // enough to look at this one's flow.
    const canAccess = DEV || canReadApprovalTrackerModule(module, permission);
    // Responding to a level is not permission based: LevelCard already limits the
    // actions to the approver that owns the row.
    const [showApprove, setShowApprove] = useState(false);
    const [showDenied, setShowDenied] = useState(false);
    const [showIssueComment, setShowIssueComment] = useState(false);
    const [modified,setModified] = useState(false)
    const [row, setRow] = useState(null);
    const [showMsg, setShowMsg] = useState(false);

    const genericCommentModals =[
        {
            show: showApprove,
            setter: setShowApprove,
            title: 'approve_request',
            setRow: setRow,
            row:row,
            status: STATUS_MODEL.APPROVED,
            module :module,
            endPoint :UpdateApprovalTrackerFlow
        },
        {
            show: showDenied,
            setter: setShowDenied,
            title: 'decline_comment',
            setRow: setRow,
            row:row,
            status: STATUS_MODEL.DENIED,
            module :module,
            endPoint :UpdateApprovalTrackerFlow
        },
        {
            show: showIssueComment,
            setter: setShowIssueComment,
            title: 'request_changes',
            setRow: setRow,
            row:row,
            status: STATUS_MODEL.CHANGES_REQUESTED,
            module :module,
            endPoint :UpdateApprovalTrackerFlow
        },
    ]
        // Changes are not requested in every module, so the modal that writes
        // that answer is only offered where the action is allowed
        .filter(({status}) => status !== STATUS_MODEL.CHANGES_REQUESTED || canRequestChanges(module));


    useEffect(() => {
        if (modified) {
            getData();
            setModified(false);
        }
    }, [modified]);

    // Refetch whenever the targeted flow changes. The route element is shared
    // across approval-tracker URLs, so navigating between flows (e.g. from a
    // notification deep-link) only changes the params without remounting; mount
    // only deps would keep the previous flow's cached levels. `refreshedAt`
    // covers re-entering the very same url, where no param changes at all.
    useEffect(() => {
        getData();
    }, [module, subjectId, approvalFlowId, refreshedAt]);

    const getData = () => {
        // Forbidden users are redirected by the view, do not hit the endpoint
        if (!canAccess) return;
        dispatch(GetApprovalTrackerProgress({
            approvalFlowId: approvalFlowId,
            subjectId: subjectId,
            module: module,
        })).then(
            ({ payload: { data: {success, code,data } } }) => {
                if (success === true) {
                    const isUserApprover = data?.levels?.some(level =>
                        level.approvers?.some(approver => approver.userId === user?.data?.id)
                    ) || false;
                    setShowMsg(isUserApprover);
                } else {
                    errorMsg(code);
                }
            }
        );
    };

    const handleOnBack = () => {
        // Deep-link entries (e.g. request-review notification) arrive without a
        // backUrl in location state; go back to the view of the module the flow
        // belongs to, and only fall back to history when the module is unknown.
        const target = backUrl?.pathname ? backUrl : getApprovalTrackerBackUrl(module, permission, DEV);
        if (!target?.pathname) {
            navigate(-1);
            return;
        }
        navigate(
            target.pathname, {
            state: target.state
        });
    }

    const currentStatus = isCanceled
    ? STATUS_MODEL.CANCELED
    : getApprovalTrackerProgress?.overallStatus?.id;

    const notSettled = getApprovalTrackerProgress?.overallStatus?.id !== STATUS_MODEL.APPROVED &&
        getApprovalTrackerProgress?.overallStatus?.id !== STATUS_MODEL.DENIED;

    const bannerProps = isCanceled ?
        {
            className: 'chip-error-code-150-100',
            name: t('invitation_has_been_canceled_it_is_not_possible_complete_approval'),
            classNameIcon: 'icon-color-error-code-700'
        }
        : isOverdue && notSettled ?
            {
                className: 'chip-error-code-150-100',
                name: t('invitation_has_expired_it_is_not_possible_complete_approval'),
                classNameIcon: 'icon-color-error-code-700'
            }
            : showMsg && notSettled ?
                {
                    className: 'chip-secondary-150-100',
                    name: t('your_approve_is_required'),
                    classNameIcon: 'icon-color-secondary-700'
                }
                : null;

    return {
        t,
        genericCommentModals,
        setShowApprove,
        setShowDenied,
        setShowIssueComment,
        setRow,
        setModified,
        modified,
        getApprovalTrackerProgress,
        user,
        module,
        isOverdue,
        handleOnBack,
        isCanceled,
        currentStatus,
        bannerProps,
        canAccess
    }
}
